import * as XLSX from "xlsx";

export const CATEGORY_HEADERS = [
  "category",
  "code",
  "description",
  "visibility",
];

export const SUBCATEGORY_HEADERS = [
  "category_code",
  "category",
  "subcode",
  "description",
  "visibility",
];

export const ITEM_HEADERS = [
  "category_code",
  "subcode",
  "item",
  "price",
  "description",
  "visibility",
  "quantity",
  "warning",
  "rating",
  "url",
  "portions",
  "sides",
  "specifications",
  "allergens",
];

export const SIDE_HEADERS = [
  "item",
  "price",
  "description",
  "quantity",
  "visibility",
  "universal",
];

export const ALLERGEN_HEADERS = ["title", "description", "icon"];

const INSTRUCTIONS = [
  ["Menu import template"],
  [""],
  ["How to use"],
  [
    "1. Fill the data sheet(s). Do not rename the header row columns.",
  ],
  [
    "2. Save the file, then click Import on the matching column (Categories, Subcategories, or Items). Use Import Menu in the toolbar for this full workbook. Universal Sides and Allergens can also be imported from their manager windows.",
  ],
  [
    "3. If you import separate files, import Categories first, then Subcategories, then Sides, then Allergens, then Items.",
  ],
  [
    "4. Leave unused columns blank. visibility is 1 (visible) or 0 (hidden) and defaults to 1.",
  ],
  [""],
  ["Categories"],
  ["category (required) — display name, e.g. Beverages"],
  [
    "code (required) — unique lowercase identifier, e.g. beverages. Spaces become underscores.",
  ],
  ["description (optional)"],
  ["visibility (optional)"],
  [""],
  ["Subcategories"],
  [
    "category_code (required) — parent category code. If omitted while a category is selected, that category is used.",
  ],
  ["category (required) — subcategory display name, e.g. Coffees"],
  ["subcode (required) — unique lowercase identifier, e.g. coffees"],
  ["description (optional)"],
  ["visibility (optional)"],
  [""],
  ["Sides (Universal Sides library)"],
  ["item (required) — side name, e.g. Onion Rings"],
  ["price (optional) — e.g. 12.00"],
  ["description, quantity, visibility (optional)"],
  [
    "universal (optional) — 1 to reuse across menu items (default), 0 for an item-specific side.",
  ],
  [""],
  ["Allergens (allergen library)"],
  ["title (required) — allergen name, e.g. Milk"],
  ["description (optional)"],
  ["icon (optional) — filename, e.g. Milk.jpg (defaults to none.jpg)"],
  [""],
  ["Items"],
  ["category_code (optional) — parent category code, for your reference"],
  [
    "subcode (required) — parent subcategory code. If omitted while a subcategory is selected, that subcategory is used.",
  ],
  ["item (required) — item name"],
  ["price (optional) — e.g. 12.50"],
  ["description, visibility, quantity, warning, rating (defaults to 3), url (optional)"],
  [
    "portions, sides, specifications (optional) — Name<>price--Name<>price--  e.g. Small<>5.00--Large<>8.00--",
  ],
  [
    "If a side name in the sides column already exists as a Universal side, that shared side is attached instead of creating a duplicate.",
  ],
  [
    "allergens (optional) — allergen titles or ids separated by -- or commas, e.g. Milk--Eggs or 2,7. Titles must match the Allergens sheet / library (case-insensitive).",
  ],
];

function sanitizeCode(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function toVisibility(value, fallback = 1) {
  if (value === undefined || value === null || String(value).trim() === "") {
    return fallback;
  }
  const s = String(value).toLowerCase().trim();
  return ["1", "true", "yes", "y", "visible", "on"].includes(s) ? 1 : 0;
}

function pick(row, aliases, fallback = "") {
  for (const key of aliases) {
    if (!Object.prototype.hasOwnProperty.call(row, key)) continue;
    const value = row[key];
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text !== "") return text;
  }
  return fallback;
}

function normalizeRowKeys(row = {}) {
  const out = {};
  Object.entries(row).forEach(([key, value]) => {
    const normalized = String(key || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    if (normalized) out[normalized] = value;
  });
  return out;
}

function isEmptyRow(row) {
  return Object.values(row).every(
    (value) => value === undefined || value === null || String(value).trim() === ""
  );
}

function headerSheet(headers) {
  const ws = XLSX.utils.aoa_to_sheet([headers]);
  ws["!cols"] = headers.map((h) => ({ wch: Math.max(14, h.length + 4) }));
  return ws;
}

function instructionsSheet() {
  const ws = XLSX.utils.aoa_to_sheet(INSTRUCTIONS);
  ws["!cols"] = [{ wch: 110 }];
  return ws;
}

function appendDataSheet(wb, name, headers) {
  XLSX.utils.book_append_sheet(wb, headerSheet(headers), name);
}

export function downloadMenuImportTemplate(kind = "full") {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, instructionsSheet(), "Instructions");

  if (kind === "categories") {
    appendDataSheet(wb, "Categories", CATEGORY_HEADERS);
    XLSX.writeFile(wb, "menu_categories_import_template.xlsx");
    return;
  }
  if (kind === "subcategories") {
    appendDataSheet(wb, "Subcategories", SUBCATEGORY_HEADERS);
    XLSX.writeFile(wb, "menu_subcategories_import_template.xlsx");
    return;
  }
  if (kind === "items") {
    appendDataSheet(wb, "Items", ITEM_HEADERS);
    XLSX.writeFile(wb, "menu_items_import_template.xlsx");
    return;
  }
  if (kind === "sides") {
    appendDataSheet(wb, "Sides", SIDE_HEADERS);
    XLSX.writeFile(wb, "menu_sides_import_template.xlsx");
    return;
  }
  if (kind === "allergens") {
    appendDataSheet(wb, "Allergens", ALLERGEN_HEADERS);
    XLSX.writeFile(wb, "menu_allergens_import_template.xlsx");
    return;
  }

  appendDataSheet(wb, "Categories", CATEGORY_HEADERS);
  appendDataSheet(wb, "Subcategories", SUBCATEGORY_HEADERS);
  appendDataSheet(wb, "Sides", SIDE_HEADERS);
  appendDataSheet(wb, "Allergens", ALLERGEN_HEADERS);
  appendDataSheet(wb, "Items", ITEM_HEADERS);
  XLSX.writeFile(wb, "menu_import_template.xlsx");
}

function findSheet(wb, names) {
  const wanted = names.map((n) => n.toLowerCase());
  const match = wb.SheetNames.find((name) =>
    wanted.includes(String(name).toLowerCase())
  );
  return match ? wb.Sheets[match] : null;
}

function sheetToRows(ws) {
  if (!ws) return [];
  return XLSX.utils
    .sheet_to_json(ws, { defval: "", raw: false })
    .map(normalizeRowKeys)
    .filter((row) => !isEmptyRow(row));
}

function mapCategoryRow(row) {
  const category = pick(row, ["category", "name", "category_name", "main", "title"]);
  const code = sanitizeCode(
    pick(row, ["code", "category_code"]) || category
  );
  return {
    id: pick(row, ["id", "category_id"]),
    category,
    code,
    description: pick(row, ["description", "desc"]),
    visibility: toVisibility(pick(row, ["visibility", "visible"])),
  };
}

function mapSubcategoryRow(row) {
  const category = pick(row, [
    "category",
    "name",
    "subcategory",
    "subcategory_name",
    "title",
  ]);
  const subcode = sanitizeCode(
    pick(row, ["subcode", "subcategory_code"]) || category
  );
  // Prefer explicit parent code; export historically wrote category_name / category_id only
  const category_code = sanitizeCode(
    pick(row, [
      "category_code",
      "parent_code",
      "parent_category_code",
      "parent",
    ])
  );
  return {
    category_code,
    category_id: pick(row, ["category_id", "parent_id"]),
    category_name: pick(row, [
      "category_name",
      "parent_name",
      "parent_category",
      "parent_category_name",
    ]),
    category,
    subcode,
    description: pick(row, ["description", "desc"]),
    visibility: toVisibility(pick(row, ["visibility", "visible"])),
  };
}

function mapItemRow(row) {
  const item = pick(row, ["item", "name", "item_name", "title"]);
  const priceRaw = pick(row, ["price", "amount", "cost"]);
  const price = priceRaw === "" ? 0 : Number(priceRaw);
  return {
    category_code: sanitizeCode(pick(row, ["category_code", "parent_code"])),
    subcode: sanitizeCode(
      pick(row, ["subcode", "subcategory_code", "subcategory"])
    ),
    item,
    price: Number.isFinite(price) ? price : 0,
    description: pick(row, ["description", "desc"]),
    visibility: toVisibility(pick(row, ["visibility", "visible"])),
    quantity: pick(row, ["quantity"], "0"),
    warning: pick(row, ["warning"], "0"),
    rating: pick(row, ["rating"], "3"),
    url: pick(row, ["url", "photo", "image"]),
    portions: pick(row, ["portions", "sizes"]),
    sides: pick(row, ["sides"]),
    specifications: pick(row, ["specifications", "specs"]),
    allergens: pick(row, [
      "allergens",
      "allergen",
      "allergen_ids",
      "allergens_ids",
      "allergens_titles",
    ]),
  };
}

function mapAllergenRow(row) {
  const title = pick(row, ["title", "name", "allergen", "item"]);
  return {
    title,
    description: pick(row, ["description", "desc"]),
    icon: pick(row, ["icon", "url", "filename"], "none.jpg") || "none.jpg",
  };
}

function mapSideRow(row) {
  const item = pick(row, ["item", "name", "side", "side_name", "title"]);
  const priceRaw = pick(row, ["price", "amount", "cost"]);
  const price = priceRaw === "" ? 0 : Number(priceRaw);
  const quantityRaw = pick(row, ["quantity", "qty", "stock"], "100");
  const quantity = parseInt(String(quantityRaw).replace(/\D/g, ""), 10);
  return {
    item,
    price: Number.isFinite(price) ? String(price) : "0",
    description: pick(row, ["description", "desc"]),
    quantity: Number.isFinite(quantity) ? quantity : 100,
    visibility: toVisibility(pick(row, ["visibility", "visible"])),
    universal: toVisibility(pick(row, ["universal", "shared"]), 1),
    subcode_id: pick(row, ["subcode_id", "subcode"], "0"),
  };
}

function looksLikeSidesHeaders(headers) {
  const set = new Set(headers);
  if (set.has("universal") && (set.has("item") || set.has("name") || set.has("side"))) {
    return true;
  }
  if (set.has("side") || set.has("side_name")) return true;
  return (
    (set.has("item") || set.has("name")) &&
    set.has("universal")
  );
}

function looksLikeAllergenHeaders(headers) {
  const set = new Set(headers);
  if (set.has("icon") && (set.has("title") || set.has("name") || set.has("allergen"))) {
    return true;
  }
  return set.has("allergen") || (set.has("title") && set.has("description") && !set.has("subcode"));
}

/** Split allergen tokens from titles/ids string. */
export function splitAllergenTokens(raw) {
  return String(raw || "")
    .split(/--|,|;|\|/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Resolve titles or numeric ids against an allergen catalog → id list. */
export function resolveAllergenIdsFromTokens(tokens, catalog = []) {
  const byTitle = new Map();
  (catalog || []).forEach((a) => {
    const title = String(a.title || a.name || "").trim().toLowerCase();
    if (title) byTitle.set(title, String(a.id));
  });
  const ids = [];
  const seen = new Set();
  const missing = [];
  for (const token of tokens || []) {
    const t = String(token || "").trim();
    if (!t) continue;
    let id = null;
    if (/^\d+$/.test(t)) {
      id = t;
    } else {
      id = byTitle.get(t.toLowerCase()) || null;
    }
    if (!id) {
      missing.push(t);
      continue;
    }
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return { ids, missing };
}

export function formatAllergenTitlesForExport(item) {
  const resolved =
    item?.allergens_resolved ||
    item?.raw?.allergens_resolved ||
    [];
  if (Array.isArray(resolved) && resolved.length) {
    return resolved
      .map((a) => String(a?.title || "").trim())
      .filter(Boolean)
      .join("--");
  }
  const titles = item?.allergens?.titles;
  if (Array.isArray(titles) && titles.length) {
    return titles.map((t) => String(t).trim()).filter(Boolean).join("--");
  }
  const fromRaw =
    item?.raw?.allergens_titles ||
    item?.allergens_titles ||
    "";
  return String(fromRaw)
    .split("--")
    .map((s) => s.trim())
    .filter(Boolean)
    .join("--");
}

function fromNestedJson(parsed) {
  const cats = Array.isArray(parsed?.categories)
    ? parsed.categories
    : Array.isArray(parsed)
    ? parsed
    : [];

  const categories = [];
  const subcategories = [];
  const items = [];
  const sides = [];
  const allergens = [];

  (Array.isArray(parsed?.sides) ? parsed.sides : []).forEach((side) => {
    const mapped = mapSideRow(normalizeRowKeys(side));
    if (mapped.item) sides.push(mapped);
  });

  (Array.isArray(parsed?.allergens) ? parsed.allergens : []).forEach((row) => {
    const mapped = mapAllergenRow(normalizeRowKeys(row));
    if (mapped.title) allergens.push(mapped);
  });

  cats.forEach((cat) => {
    const mappedCat = mapCategoryRow(normalizeRowKeys(cat));
    if (mappedCat.category || mappedCat.code) categories.push(mappedCat);
    (cat.subcategories || []).forEach((sub) => {
      const mappedSub = mapSubcategoryRow({
        ...normalizeRowKeys(sub),
        category_code: mappedCat.code,
        category: sub.name ?? sub.category ?? sub.main,
      });
      if (mappedSub.category || mappedSub.subcode) subcategories.push(mappedSub);
      (sub.items || []).forEach((item) => {
        const raw = item?.raw && typeof item.raw === "object" ? item.raw : item;
        const mappedItem = mapItemRow({
          ...normalizeRowKeys(raw),
          subcode: raw.subcode ?? sub.subcode ?? mappedSub.subcode,
          item: raw.item ?? item.name ?? raw.name,
          allergens:
            raw.allergens ||
            formatAllergenTitlesForExport(item) ||
            raw.allergens_titles ||
            "",
        });
        if (mappedItem.item) items.push(mappedItem);
      });
    });
  });

  return { categories, subcategories, items, sides, allergens };
}

export async function parseMenuImportFile(file, { kind } = {}) {
  if (!file) throw new Error("No file selected");
  const name = String(file.name || "").toLowerCase();

  if (name.endsWith(".json")) {
    const text = await file.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error("Invalid JSON file");
    }
    return fromNestedJson(parsed);
  }

  const isSpreadsheet =
    name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv");
  if (!isSpreadsheet) {
    throw new Error("Unsupported file type. Use .xlsx, .csv, or .json");
  }

  const data = name.endsWith(".csv")
    ? (await file.text()).replace(/^\uFEFF/, "")
    : await file.arrayBuffer();
  const wb = XLSX.read(data, {
    type: name.endsWith(".csv") ? "string" : "array",
    raw: false,
  });

  const empty = {
    categories: [],
    subcategories: [],
    items: [],
    sides: [],
    allergens: [],
  };

  const sideSheet = findSheet(wb, [
    "sides",
    "side",
    "universal sides",
    "universal_sides",
    "menu_sides",
  ]);
  const allergenSheet = findSheet(wb, [
    "allergens",
    "allergen",
    "allergens_list",
    "allergen library",
  ]);
  const categorySheet =
    findSheet(wb, ["categories", "category"]) ||
    (wb.SheetNames.length === 1 && kind === "categories"
      ? wb.Sheets[wb.SheetNames[0]]
      : null);
  const subcategorySheet =
    findSheet(wb, ["subcategories", "subcategory"]) ||
    (wb.SheetNames.length === 1 && kind === "subcategories"
      ? wb.Sheets[wb.SheetNames[0]]
      : null);
  const itemSheet =
    findSheet(wb, ["items", "item", "menu items", "menu_items"]) ||
    (wb.SheetNames.length === 1 && kind === "items"
      ? wb.Sheets[wb.SheetNames[0]]
      : null);

  const categories = sheetToRows(categorySheet).map(mapCategoryRow);
  const subcategories = sheetToRows(subcategorySheet).map(mapSubcategoryRow);
  const items = sheetToRows(itemSheet).map(mapItemRow);
  const sides = sheetToRows(
    sideSheet ||
      (wb.SheetNames.length === 1 && kind === "sides"
        ? wb.Sheets[wb.SheetNames[0]]
        : null)
  ).map(mapSideRow);
  const allergens = sheetToRows(
    allergenSheet ||
      (wb.SheetNames.length === 1 && kind === "allergens"
        ? wb.Sheets[wb.SheetNames[0]]
        : null)
  ).map(mapAllergenRow);

  // Single-sheet CSV/XLSX: infer entity from headers when sheet names are generic
  if (
    wb.SheetNames.length === 1 &&
    !findSheet(wb, [
      "categories",
      "subcategories",
      "items",
      "sides",
      "universal sides",
      "allergens",
      "allergen",
    ])
  ) {
    const rows = sheetToRows(wb.Sheets[wb.SheetNames[0]]);
    const headers = Object.keys(rows[0] || {});
    if (kind === "allergens" || looksLikeAllergenHeaders(headers)) {
      return { ...empty, allergens: rows.map(mapAllergenRow) };
    }
    if (kind === "sides" || looksLikeSidesHeaders(headers)) {
      return { ...empty, sides: rows.map(mapSideRow) };
    }
    if (kind === "items" || headers.includes("item") || headers.includes("price")) {
      return { ...empty, items: rows.map(mapItemRow) };
    }
    if (
      kind === "subcategories" ||
      (headers.includes("subcode") && headers.includes("category_code"))
    ) {
      return { ...empty, subcategories: rows.map(mapSubcategoryRow) };
    }
    if (kind === "categories" || headers.includes("category") || headers.includes("code")) {
      return { ...empty, categories: rows.map(mapCategoryRow) };
    }
  }

  return { categories, subcategories, items, sides, allergens };
}

function resultOk(value) {
  if (value && typeof value === "object") {
    return (
      value.ok === true ||
      String(value.status || "").toLowerCase() === "success" ||
      String(value.message || "").toLowerCase() === "exists" ||
      String(value.message || "").toLowerCase() === "created"
    );
  }
  const text = String(value ?? "").trim().toLowerCase();
  return text === "success" || text === "exists" || text === "";
}

function wasExists(value) {
  if (value && typeof value === "object") {
    return (
      value.exists === true ||
      String(value.message || "").toLowerCase() === "exists"
    );
  }
  return String(value ?? "").trim().toLowerCase() === "exists";
}

export async function applyMenuImport({
  parsed,
  kind = "full",
  tenantServerUrl,
  selectedCategory,
  selectedSubcategory,
  addMainCategory,
  addSubcategoryApi,
  createMenuItem,
  addSideMenuItem,
  addAllergen,
  fetchAllergens,
  username,
}) {
  const summary = {
    created: {
      categories: 0,
      subcategories: 0,
      items: 0,
      sides: 0,
      allergens: 0,
    },
    skipped: 0,
    errors: [],
  };

  const includeCategories = kind === "full" || kind === "categories";
  const includeSubcategories = kind === "full" || kind === "subcategories";
  const includeSides = kind === "full" || kind === "sides";
  const includeAllergens = kind === "full" || kind === "allergens";
  const includeItems = kind === "full" || kind === "items";

  const importedCategoryNames = {};

  if (includeCategories) {
    for (const [index, row] of (parsed.categories || []).entries()) {
      if (!row.category || !row.code) {
        summary.errors.push(
          `Categories row ${index + 2}: category and code are required`
        );
        continue;
      }
      try {
        const res = await addMainCategory(tenantServerUrl, {
          category: row.category,
          code: row.code,
          description: row.description,
          visibility: row.visibility,
          username,
        });
        if (wasExists(res)) {
          summary.skipped += 1;
        } else if (resultOk(res)) {
          summary.created.categories += 1;
        } else {
          throw new Error(String(res || "failed"));
        }
        importedCategoryNames[row.code] = row.category;
      } catch (err) {
        summary.errors.push(
          `Category "${row.category}": ${err?.message || err}`
        );
      }
    }
  }

  if (includeSubcategories) {
    const categoryByName = {};
    const categoryById = {};
    for (const cat of parsed.categories || []) {
      if (!cat.code) continue;
      if (cat.category) {
        categoryByName[String(cat.category).trim().toLowerCase()] = cat.code;
      }
      categoryByName[String(cat.code).trim().toLowerCase()] = cat.code;
      if (cat.id) categoryById[String(cat.id)] = cat.code;
    }
    Object.entries(importedCategoryNames).forEach(([code, name]) => {
      categoryByName[String(code).trim().toLowerCase()] = code;
      if (name) categoryByName[String(name).trim().toLowerCase()] = code;
    });

    for (const [index, row] of (parsed.subcategories || []).entries()) {
      let parentCode =
        row.category_code || sanitizeCode(selectedCategory?.code || "");
      if (!parentCode && row.category_name) {
        parentCode =
          categoryByName[String(row.category_name).trim().toLowerCase()] || "";
      }
      if (!parentCode && row.category_id) {
        parentCode = categoryById[String(row.category_id)] || "";
      }

      const parentName =
        importedCategoryNames[parentCode] ||
        row.category_name ||
        selectedCategory?.name ||
        parentCode;
      if (!row.category || !row.subcode) {
        summary.errors.push(
          `Subcategories row ${index + 2}: category and subcode are required`
        );
        continue;
      }
      if (!parentCode) {
        summary.errors.push(
          `Subcategories row ${index + 2}: category_code is required (or select a category first)`
        );
        continue;
      }
      try {
        const res = await addSubcategoryApi(tenantServerUrl, {
          main: parentName,
          code: parentCode,
          category: row.category,
          subcode: row.subcode,
          description: row.description,
          visibility: row.visibility,
          bywho: username,
        });
        if (wasExists(res)) summary.skipped += 1;
        else if (resultOk(res)) summary.created.subcategories += 1;
        else throw new Error(String(res || "failed"));
      } catch (err) {
        summary.errors.push(
          `Subcategory "${row.category}": ${err?.message || err}`
        );
      }
    }
  }

  if (includeSides) {
    if (typeof addSideMenuItem !== "function") {
      summary.errors.push("Sides import is not available");
    } else {
      for (const [index, row] of (parsed.sides || []).entries()) {
        if (!row.item) {
          summary.errors.push(`Sides row ${index + 2}: item name is required`);
          continue;
        }
        try {
          const res = await addSideMenuItem(tenantServerUrl, {
            item: row.item,
            price: row.price,
            description: row.description,
            quantity: row.quantity,
            visibility: row.visibility,
            universal: row.universal,
            subcode_id: row.subcode_id || 0,
            bywho: username,
          });
          if (wasExists(res)) summary.skipped += 1;
          else if (resultOk(res)) summary.created.sides += 1;
          else throw new Error(String(res?.message || res || "failed"));
        } catch (err) {
          summary.errors.push(`Side "${row.item}": ${err?.message || err}`);
        }
      }
    }
  }

  if (includeAllergens) {
    if (typeof addAllergen !== "function") {
      summary.errors.push("Allergens import is not available");
    } else {
      for (const [index, row] of (parsed.allergens || []).entries()) {
        if (!row.title) {
          summary.errors.push(
            `Allergens row ${index + 2}: title is required`
          );
          continue;
        }
        try {
          const res = await addAllergen(tenantServerUrl, {
            title: row.title,
            description: row.description,
            icon: row.icon,
            bywho: username,
          });
          if (wasExists(res)) summary.skipped += 1;
          else if (resultOk(res)) summary.created.allergens += 1;
          else throw new Error(String(res?.message || res || "failed"));
        } catch (err) {
          summary.errors.push(
            `Allergen "${row.title}": ${err?.message || err}`
          );
        }
      }
    }
  }

  let allergenCatalog = [];
  const needsAllergenResolve =
    includeItems &&
    (parsed.items || []).some((row) => String(row.allergens || "").trim());
  if (needsAllergenResolve && typeof fetchAllergens === "function") {
    try {
      allergenCatalog = await fetchAllergens(tenantServerUrl);
    } catch (err) {
      summary.errors.push(
        `Could not load allergen library for item attach: ${err?.message || err}`
      );
    }
  }

  if (includeItems) {
    for (const [index, row] of (parsed.items || []).entries()) {
      const subcode =
        row.subcode || sanitizeCode(selectedSubcategory?.subcode || "");
      if (!row.item) {
        summary.errors.push(`Items row ${index + 2}: item name is required`);
        continue;
      }
      if (!subcode) {
        summary.errors.push(
          `Items row ${index + 2}: subcode is required (or select a subcategory first)`
        );
        continue;
      }
      try {
        let allergen_ids = "";
        if (String(row.allergens || "").trim()) {
          const { ids, missing } = resolveAllergenIdsFromTokens(
            splitAllergenTokens(row.allergens),
            allergenCatalog
          );
          if (missing.length) {
            summary.errors.push(
              `Item "${row.item}": unknown allergen(s): ${missing.join(", ")}`
            );
          }
          allergen_ids = ids.join(",");
        }
        const res = await createMenuItem(tenantServerUrl, {
          code: row.category_code || selectedCategory?.code || "",
          subcode,
          item: row.item,
          price: row.price,
          description: row.description,
          visibility: row.visibility,
          username,
          url: row.url,
          rating: row.rating,
          quantity: row.quantity,
          warning: row.warning,
          sizes: row.portions,
          sides: row.sides,
          specifications: row.specifications,
          allergen_ids,
        });
        if (wasExists(res)) summary.skipped += 1;
        else if (resultOk(res)) summary.created.items += 1;
        else throw new Error(String(res || "failed"));
      } catch (err) {
        summary.errors.push(`Item "${row.item}": ${err?.message || err}`);
      }
    }
  }

  return summary;
}

export function formatImportSummary(summary) {
  const { created, skipped, errors } = summary;
  const parts = [];
  if (created.categories)
    parts.push(
      `${created.categories} categor${created.categories === 1 ? "y" : "ies"}`
    );
  if (created.subcategories)
    parts.push(
      `${created.subcategories} subcategor${
        created.subcategories === 1 ? "y" : "ies"
      }`
    );
  if (created.items)
    parts.push(`${created.items} item${created.items === 1 ? "" : "s"}`);
  if (created.sides)
    parts.push(`${created.sides} side${created.sides === 1 ? "" : "s"}`);
  if (created.allergens)
    parts.push(
      `${created.allergens} allergen${created.allergens === 1 ? "" : "s"}`
    );

  const createdText = parts.length
    ? `Imported ${parts.join(", ")}.`
    : "No new rows were imported.";
  const skippedText = skipped
    ? ` Skipped ${skipped} existing row${skipped === 1 ? "" : "s"}.`
    : "";
  const errorText = errors.length
    ? `\n\n${errors.length} error${errors.length === 1 ? "" : "s"}:\n- ${errors
        .slice(0, 8)
        .join("\n- ")}${errors.length > 8 ? "\n- …" : ""}`
    : "";

  return `${createdText}${skippedText}${errorText}`;
}
